import React, { useEffect, useRef, useState, useCallback } from 'react';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './MelodySelect.css';

// C major pentatonic across 3 octaves — 15 notes, evenly bright/sad-free.
// Hz values are standard A=440 equal temperament.
const PENTATONIC: { name: string; hz: number }[] = [
  { name: 'C3',  hz: 130.81 }, { name: 'D3',  hz: 146.83 },
  { name: 'E3',  hz: 164.81 }, { name: 'G3',  hz: 196.00 },
  { name: 'A3',  hz: 220.00 },
  { name: 'C4',  hz: 261.63 }, { name: 'D4',  hz: 293.66 },
  { name: 'E4',  hz: 329.63 }, { name: 'G4',  hz: 392.00 },
  { name: 'A4',  hz: 440.00 },
  { name: 'C5',  hz: 523.25 }, { name: 'D5',  hz: 587.33 },
  { name: 'E5',  hz: 659.25 }, { name: 'G5',  hz: 783.99 },
  { name: 'A5',  hz: 880.00 },
];

// Single shared text used as both the playable surface and the manifesto.
const BODY_PARAGRAPHS = [
  'Selection has always been the quietest gesture on the web. You drag, you copy, you carry words elsewhere — and the page never reacts. It just yields.',
  'Here the gesture composes. Each character maps to a note in a pentatonic scale, the drag speed becomes tempo, and the length of your selection sets the sustain. You are not copying. You are conducting.',
  'Double-click a word and the chorus answers: every letter of the word sounds at once, ringing into a slow tail. The familiar text-cursor turns into a baton; the paragraph below is the score; the reader, for a moment, is the orchestra.',
  'Try whispering through a sentence. Try shouting across a line break. Try one slow word. The melody you discover here was waiting in the alphabet all along, hidden inside something as ordinary as a copy.',
];

interface FloatLabel {
  id: number;
  text: string;
  x: number;
  y: number;
}

interface ChorusPulse {
  id: number;
  x: number;
  y: number;
}

const charToNote = (ch: string) => PENTATONIC[ch.charCodeAt(0) % PENTATONIC.length];

const MelodySelect: React.FC = () => {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const reverbBusRef = useRef<GainNode | null>(null);
  const reverbDelaysRef = useRef<{ delay: DelayNode; gain: GainNode }[]>([]);

  const lastSelLenRef = useRef(0);
  const lastSelTimeRef = useRef(0);
  const lastNoteAtRef = useRef(0); // audioCtx.currentTime of last scheduled note

  const idRef = useRef(0);
  const [gateOpen, setGateOpen] = useState(true);
  const [floats, setFloats] = useState<FloatLabel[]>([]);
  const [pulses, setPulses] = useState<ChorusPulse[]>([]);
  const [hudNote, setHudNote] = useState('—');
  const [hudTempo, setHudTempo] = useState(0);
  const [hudSelLen, setHudSelLen] = useState(0);

  const exp = experiments.find(e => e.id === 'melody-select');

  // ── Audio graph setup (synchronous — must run inside a user gesture) ──
  // iOS Safari is strict: any `await` inside the touch/click handler can
  // sever the user-activation context, leaving the AudioContext silent.
  // We also play a 1-sample silent buffer here, which is the canonical
  // way to fully unlock audio on iOS.
  const ensureAudio = useCallback(() => {
    let ctx = audioCtxRef.current;
    if (!ctx) {
      ctx = new (window.AudioContext || (window as any).webkitAudioContext)();

      const master = ctx.createGain();
      master.gain.value = 0.5;
      master.connect(ctx.destination);

      const bus = ctx.createGain();
      bus.gain.value = 0.0;
      bus.connect(master);

      const taps = [
        { time: 0.13, level: 0.55 },
        { time: 0.27, level: 0.38 },
        { time: 0.52, level: 0.22 },
        { time: 0.85, level: 0.12 },
      ].map(({ time, level }) => {
        const d = ctx!.createDelay(2);
        d.delayTime.value = time;
        const g = ctx!.createGain();
        g.gain.value = level;
        d.connect(g).connect(bus);
        return { delay: d, gain: g };
      });

      audioCtxRef.current = ctx;
      masterGainRef.current = master;
      reverbBusRef.current = bus;
      reverbDelaysRef.current = taps;
    }

    // iOS unlock: play a silent buffer in the gesture.
    try {
      const buffer = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(ctx.destination);
      src.start(0);
    } catch {/* ignore */}

    // Fire-and-forget resume (don't await — would break iOS gesture chain).
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    return ctx;
  }, []);

  // ── Schedule one note ────────────────────────────────
  const playNote = useCallback((
    ch: string,
    when: number,
    sustainSec: number,
    velocity = 0.4,
    chorus = false,
  ) => {
    const ctx = audioCtxRef.current;
    const master = masterGainRef.current;
    const bus = reverbBusRef.current;
    const taps = reverbDelaysRef.current;
    if (!ctx || !master || !bus) return null;

    const note = charToNote(ch);
    if (!note) return null;

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = note.hz;

    const env = ctx.createGain();
    const peak = velocity;
    const attack = 0.008;
    const release = Math.max(0.18, sustainSec);

    env.gain.setValueAtTime(0.0001, when);
    env.gain.exponentialRampToValueAtTime(peak, when + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, when + attack + release);

    osc.connect(env);
    env.connect(master);

    if (chorus) {
      // Briefly open the reverb bus and feed this note into the taps
      bus.gain.cancelScheduledValues(when);
      bus.gain.setValueAtTime(0.45, when);
      bus.gain.exponentialRampToValueAtTime(0.0001, when + 2.0);
      taps.forEach(t => env.connect(t.delay));
    }

    osc.start(when);
    osc.stop(when + attack + release + 0.05);
    osc.onended = () => {
      try { osc.disconnect(); env.disconnect(); } catch {}
    };

    return note;
  }, []);

  // ── Visual: floating note label near current selection ──
  const flashNote = useCallback((name: string, clientX: number, clientY: number) => {
    const id = ++idRef.current;
    setFloats(prev => [...prev.slice(-7), { id, text: name, x: clientX, y: clientY }]);
    window.setTimeout(() => setFloats(prev => prev.filter(f => f.id !== id)), 700);
  }, []);

  // ── Selection-driven playback ────────────────────────
  useEffect(() => {
    if (gateOpen) return;

    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const text = sel.toString();

      if (text.length === 0) {
        lastSelLenRef.current = 0;
        return;
      }

      const now = performance.now();

      if (text.length <= lastSelLenRef.current) {
        // shrinking selection — sync without playing
        lastSelLenRef.current = text.length;
        lastSelTimeRef.current = now;
        setHudSelLen(text.length);
        return;
      }

      const newChars = text.slice(lastSelLenRef.current);
      const dt = Math.max(8, now - (lastSelTimeRef.current || now));
      const perChar = dt / newChars.length;          // ms between newly-added chars
      const tempoMs = Math.max(40, Math.min(260, perChar));
      const sustainSec = Math.max(0.15, Math.min(0.7, text.length / 120));

      setHudTempo(Math.round(60000 / tempoMs)); // notes-per-minute
      setHudSelLen(text.length);

      const ctx = audioCtxRef.current;
      if (!ctx) return;

      // Throttle: if the burst is huge (large jump), cap to last 8 chars
      const chars = newChars.length > 12 ? newChars.slice(-12) : newChars;

      // Anchor to either now or the previous scheduled tail, whichever is later
      let scheduleAt = Math.max(ctx.currentTime + 0.001, lastNoteAtRef.current);
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      for (let i = 0; i < chars.length; i++) {
        const ch = chars[i];
        if (!/\S/.test(ch)) {
          scheduleAt += tempoMs / 1000;
          continue;
        }
        const note = playNote(ch, scheduleAt, sustainSec, 0.35);
        if (note && i === chars.length - 1) {
          setHudNote(note.name);
          flashNote(note.name, rect.right || window.innerWidth / 2, rect.top || window.innerHeight / 2);
        }
        scheduleAt += tempoMs / 1000;
      }

      lastNoteAtRef.current = scheduleAt;
      lastSelLenRef.current = text.length;
      lastSelTimeRef.current = now;
    };

    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [gateOpen, playNote, flashNote]);

  // ── Double-click → chorus on the clicked word ────────
  useEffect(() => {
    if (gateOpen) return;

    const onDblClick = (e: MouseEvent) => {
      const sel = window.getSelection();
      const word = sel?.toString().trim() ?? '';
      if (!word) return;

      const ctx = audioCtxRef.current;
      if (!ctx) return;

      const id = ++idRef.current;
      setPulses(prev => [...prev, { id, x: e.clientX, y: e.clientY }]);
      window.setTimeout(() => setPulses(prev => prev.filter(p => p.id !== id)), 1600);

      // Play all letters of the word at once with chorus + slight stagger for body
      const sustain = Math.min(1.2, 0.4 + word.length * 0.04);
      const t0 = ctx.currentTime + 0.01;
      for (let i = 0; i < word.length; i++) {
        const ch = word[i];
        if (!/\S/.test(ch)) continue;
        playNote(ch, t0 + i * 0.012, sustain, 0.28, true);
      }
      setHudNote(`${word.slice(0, 6).toUpperCase()}…`);
    };

    document.addEventListener('dblclick', onDblClick);
    return () => document.removeEventListener('dblclick', onDblClick);
  }, [gateOpen, playNote]);

  // ── Cleanup ──────────────────────────────────────────
  useEffect(() => {
    return () => {
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
      masterGainRef.current = null;
      reverbBusRef.current = null;
      reverbDelaysRef.current = [];
    };
  }, []);

  // ── Gate handler (must stay synchronous for iOS audio unlock) ─────
  const openGate = useCallback(() => {
    ensureAudio();
    setGateOpen(false);
  }, [ensureAudio]);

  return (
    <div className="melodySelect">
      <ExperimentNav prompt={exp?.prompt} title={exp?.title} />

      <div className="melodyContainer">
        <header className="melodyHeader">
          <div className="melodyTag">EXP / 13 / 2026.05</div>
          <h1 className="melodyTitle">MELODY SELECT</h1>
          <p className="melodyLede">
            Drag text. The selection sings.
          </p>
        </header>

        <article className="melodyBody">
          {BODY_PARAGRAPHS.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </article>

        <div className="melodyHint">
          ▸ select text to play · double-click a word for chorus
        </div>
      </div>

      {/* Floating note labels */}
      {floats.map(f => (
        <span
          key={f.id}
          className="melodyNoteFloat"
          style={{ left: f.x, top: f.y }}
        >
          {f.text}
        </span>
      ))}

      {/* Chorus pulses */}
      {pulses.map(p => (
        <span
          key={p.id}
          className="melodyChorusPulse"
          style={{ left: p.x, top: p.y }}
        />
      ))}

      {/* HUD */}
      {!gateOpen && (
        <div className="melodyHud">
          <span>note <span className="hudValue">{hudNote}</span></span>
          <span>nps <span className="hudValue">{hudTempo}</span></span>
          <span>sel <span className="hudValue">{hudSelLen}</span></span>
        </div>
      )}

      {/* Audio gate */}
      {gateOpen && (
        <div className="melodyGateOverlay" onClick={openGate}>
          <div className="melodyGateBox">
            <div className="melodyGateIcon">♬</div>
            <div className="melodyGateTitle">tap to enable audio</div>
            <div className="melodyGateDesc">
              the page composes from the text you select<br />
              drag · double-click · listen
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MelodySelect;
